<template>
    <div class="flex flex-col gap-sm mobile-nav relative h-full flex-1">
        <nav
            v-if="isMenuOpen"
            aria-hidden="false"
        >
            <ul
                v-for="(menu, level) in menuStack"
                :key="level"
                ref="menuRefs"
                class="w-full bg-white z-50 overflow-auto !max-w-full"
                :class="{'absolute top-0 right-0 h-full transition duration-300 ease-in-out w-full': level > 0}"
                :style="{
                    opacity: level === menuStack.length - 1 ? 1 : 0,
                    zIndex: level
                }"
                :aria-hidden="level < menuStack.length - 1"
                role="menu"
                tabindex="-1"
            >
                <template v-if="menuStack.length > 1">
                    <li role="none">
                        <button
                            class="flex items-center gap-3 w-full text-link text-xl min-h-10 py-2 px-4 border-b hover:bg-link hover:text-link-content"
                            aria-label="Go back"
                            role="menuitem"
                            @click="goBack"
                        >
                            <MiscIcon name="chevron-left" size="lg"/>
                            <span class="min-h-10 flex items-center">{{ t('menu.back') }}</span>
                        </button>
                    </li>

                    <li v-if="getCurrentParent(level)" role="none">
                        <div class="flex items-center gap-3 w-full text-link text-xl min-h-10 py-2 px-4 border-b">
                            <span class="min-h-10 flex items-center font-bold">
                                {{ getCurrentParent(level)?.name }}
                            </span>
                        </div>
                    </li>

                    <li v-if="getCurrentParent(level)" role="none">
                        <MiscLink
                            :to="checkUrl(getCurrentParent(level)?.url, getCurrentParent(level)?.id ?? '')"
                            class="flex justify-between items-center gap-3 w-full text-link text-xl min-h-10 py-2 px-4 border-b hover:bg-link hover:text-link-content"
                        >
                            <span class="min-h-10 flex items-center">{{ t('menu.showAll') }}</span>
                            <MiscIcon name="chevron-right" size="lg"/>
                        </MiscLink>
                    </li>
                </template>

                <li v-for="(item, index) in menu" :key="index" role="none">
                    <button
                        v-if="item.children?.length > 0"
                        class="flex justify-between items-center gap-3 w-full text-link text-xl min-h-10 py-2 px-4 border-b hover:bg-link hover:text-link-content"
                        :class="{
                            'bg-secondary text-secondary-content': isActive(item.url)
                        }"
                        role="menuitem"
                        :aria-haspopup="true"
                        :aria-expanded="menuStack.length - 1 === level"
                        @click="openSubmenu(item)"
                    >
                        <div class="flex items-center gap-2">
                            <div v-if="item?.navIcon" class="w-8 h-8">
                                <MiscSvgLoader :icon="item?.navIcon" class="h-8 w-8"/>
                            </div>
                            <span class="min-h-10 flex items-center">{{ item.name }}</span>
                        </div>
                        <MiscIcon name="chevron-right" size="lg"/>
                    </button>
                    <MiscLink
                        v-else
                        :to="item.externalLink ? item.externalLink : checkUrl(item.url, item.id)"
                        class="flex justify-between items-center gap-3 w-full text-link text-xl min-h-10 py-2 px-4 border-b hover:bg-link hover:text-link-content"
                        :class="{
                            'bg-secondary text-secondary-content': isActive(item.url)
                        }"
                        role="menuitem"
                    >
                        <div class="flex items-center gap-2">
                            <MiscSvgLoader :icon="item?.navIcon" class="h-8 w-8"/>
                            <span class="min-h-10 flex items-center">{{ item.name }}</span>
                        </div>
                    </MiscLink>
                </li>

                <li v-if="menuStack.length == 1" role="none">
                    <MiscLink
                        v-if="data?.saleTitle?.value"
                        :to="data?.saleUrl?.value"
                        role="menuitem"
                        :target="data?.saleMenu1NewTab?.value ? '_blank' : null"
                        class="flex justify-between items-center gap-3 w-full text-link text-xl min-h-10 py-2 px-4 border-b hover:bg-link hover:text-link-content"
                    >
                        <div class="flex gap-4">
                            <span class="min-h-10 flex items-center">{{ data?.saleTitle?.value }}</span>
                        </div>
                    </MiscLink>
                </li>
            </ul>
        </nav>

        <LayoutHeaderTopLinks v-if="menuStack.length === 1" :data="data" link-class="leading-6 py-2 px-4 text-link block border-b"/>
    </div>
</template>

<script setup lang="ts">
import {ref, nextTick, onMounted, onUnmounted} from 'vue'
import type {HblNavigation} from "@/utils/types";
import { useRoute } from 'vue-router'
import {useI18n} from 'vue-i18n'

const {t} = useI18n()

const props = defineProps<{
    navigation: HblNavigation | null
    data: never | null
}>()

interface MenuItem {
    id: string
    name: string
    externalLink?: boolean
    linkNewTab?: boolean
    url?: string
    navIcon?: string
    children?: MenuItem[]
}

const menuItems: MenuItem[] = props.navigation

const isMenuOpen = ref(true)
const menuStack = ref<MenuItem[][]>([menuItems])
const menuRefs = ref<HTMLElement[]>([])
const route = useRoute()

const openSubmenu = (item: MenuItem) => {
    if (item.children) {
        menuStack.value.push(item.children)
        nextTick(() => focusOnNewMenu())
    }
}

const goBack = () => {
    if (menuStack.value.length > 1) {
        menuStack.value.pop()
        nextTick(() => focusOnNewMenu())
    }
}

const focusOnNewMenu = () => {
    const lastMenu = menuRefs.value[menuStack.value.length - 1]
    if (lastMenu) lastMenu.focus()
}

const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && menuStack.value.length > 1) {
        goBack()
    }
}

function isActive(itemUrl: string | undefined): boolean {
    if (!itemUrl) return false
    return route.path.startsWith(itemUrl)
}

function checkUrl(url: string | undefined, id: string): string {
    return url || '/navigation/' + id
}

function getCurrentParent(level: number): MenuItem | undefined {
    const parentLevel = level - 1
    const current = menuStack.value[level]
    const parent = menuStack.value[parentLevel]
    if (!current || !parent) return undefined

    return parent.find(item =>
        item.children?.some(child => current.some(c => c.id === child.id))
    )
}

onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown)
})
</script>
